import { useEffect, useRef } from 'react';
import { AddressFormData, SavedAddress } from '../hooks/useCheckout';
import { useIller, useIlceler } from '@/hooks/useIlIlce';

interface Props {
  data: AddressFormData;
  errors: Partial<AddressFormData>;
  onChange: (data: AddressFormData) => void;
  onNext: () => void;
  savedAddresses?: SavedAddress[];
  selectedAddressId?: string | null;
  onSelectAddress?: (addr: SavedAddress) => void;
}

export default function AddressForm({ data, errors, onChange, onNext, savedAddresses = [], selectedAddressId, onSelectAddress }: Props) {
  const { iller, loading: illerLoading } = useIller();
  const { ilceler, loading: ilcelerLoading } = useIlceler(data.il_kodu);

  // İl değişince ilçeyi sıfırla (kullanıcı manuel seçim yaptığında)
  const prevIlKoduRef = useRef(data.il_kodu);
  useEffect(() => {
    // Sadece il_kodu değiştiğinde ve önceki değer boş değilse (manuel seçim)
    if (data.il_kodu && prevIlKoduRef.current && prevIlKoduRef.current !== data.il_kodu) {
      onChange({ ...data, ilce: '' });
    }
    prevIlKoduRef.current = data.il_kodu;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.il_kodu]);

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 text-sm bg-cream rounded-xl border ${hasError ? 'border-red-400' : 'border-transparent'} focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee`;

  const selectClass = (hasError: boolean) =>
    `w-full px-4 py-3 text-sm bg-cream rounded-xl border ${hasError ? 'border-red-400' : 'border-transparent'} focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee cursor-pointer appearance-none`;

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="font-serif text-xl font-semibold text-coffee mb-6">Teslimat Adresi</h2>

      {/* Kayıtlı Adresler */}
      {savedAddresses.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-medium text-coffee-medium mb-3">Kayıtlı Adreslerim</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {savedAddresses.map(addr => {
              const isSelected = selectedAddressId === addr.id;
              return (
                <button
                  key={addr.id}
                  type="button"
                  onClick={() => onSelectAddress?.(addr)}
                  className={`text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    isSelected
                      ? 'border-gold bg-gold/5'
                      : 'border-cream-dark bg-cream hover:border-gold/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-xs font-semibold text-coffee">
                      {addr.adres_basligi}
                    </span>
                    {addr.varsayilan && (
                      <span className="text-xs bg-gold/10 text-gold px-1.5 py-0.5 rounded-full whitespace-nowrap">Varsayılan</span>
                    )}
                    {isSelected && (
                      <div className="w-4 h-4 flex items-center justify-center bg-gold rounded-full flex-shrink-0">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-coffee-light leading-relaxed line-clamp-2">{addr.adres}</p>
                  <p className="text-xs text-coffee-light mt-0.5">{addr.ilce} / {addr.il_adi}</p>
                </button>
              );
            })}
          </div>
          <div className="mt-3 border-t border-cream-dark pt-4">
            <p className="text-xs text-coffee-light">Bilgileri düzenleyebilirsiniz:</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Ad Soyad - tek kutu, tam genişlik */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">Ad Soyad</label>
          <input
            type="text"
            value={data.ad_soyad}
            onChange={e => onChange({ ...data, ad_soyad: e.target.value })}
            placeholder="Ad Soyad"
            className={inputClass(!!errors.ad_soyad)}
          />
          {errors.ad_soyad && <p className="text-xs text-red-500 mt-1">{errors.ad_soyad}</p>}
        </div>

        {/* Telefon */}
        <div>
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">Telefon</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-coffee-medium select-none pointer-events-none">
              +90
            </span>
            <input
              type="tel"
              value={data.telefon}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                let formatted = digits;
                if (digits.length > 3 && digits.length <= 6) {
                  formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
                } else if (digits.length > 6 && digits.length <= 8) {
                  formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
                } else if (digits.length > 8) {
                  formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
                }
                onChange({ ...data, telefon: formatted });
              }}
              placeholder="5XX XXX XX XX"
              maxLength={13}
              className={`${inputClass(!!errors.telefon)} pl-12`}
            />
          </div>
          {errors.telefon && <p className="text-xs text-red-500 mt-1">{errors.telefon}</p>}
        </div>

        {/* E-posta */}
        <div>
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">E-posta</label>
          <input
            type="email"
            value={data.email}
            onChange={e => onChange({ ...data, email: e.target.value })}
            placeholder="E-posta"
            className={inputClass(!!errors.email)}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>

        {/* İl */}
        <div>
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">İl</label>
          <div className="relative">
            <select
              value={data.il_kodu}
              onChange={e => {
                const selected = iller.find(i => i.il_kodu === e.target.value);
                onChange({ ...data, il_kodu: e.target.value, il_adi: selected?.il_adi ?? '', ilce: '' });
              }}
              className={selectClass(!!errors.il_kodu)}
              disabled={illerLoading}
            >
              <option value="">İl seçin</option>
              {iller.map(il => (
                <option key={il.il_kodu} value={il.il_kodu}>{il.il_adi}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <i className="ri-arrow-down-s-line text-coffee-medium text-lg" />
            </div>
          </div>
          {errors.il_kodu && <p className="text-xs text-red-500 mt-1">{errors.il_kodu}</p>}
        </div>

        {/* İlçe */}
        <div>
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">İlçe</label>
          <div className="relative">
            <select
              value={data.ilce}
              onChange={e => onChange({ ...data, ilce: e.target.value })}
              className={selectClass(!!errors.ilce)}
              disabled={!data.il_kodu || ilcelerLoading}
            >
              <option value="">{data.il_kodu ? 'İlçe seçin' : 'Önce il seçin'}</option>
              {ilceler.map(ilce => (
                <option key={ilce.id} value={ilce.ilce_adi}>{ilce.ilce_adi}</option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
              <i className="ri-arrow-down-s-line text-coffee-medium text-lg" />
            </div>
          </div>
          {errors.ilce && <p className="text-xs text-red-500 mt-1">{errors.ilce}</p>}
        </div>

        {/* Adres */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">Adres</label>
          <input
            type="text"
            value={data.adres}
            onChange={e => onChange({ ...data, adres: e.target.value })}
            placeholder="Mahalle, sokak, bina no, daire no"
            className={inputClass(!!errors.adres)}
          />
          {errors.adres && <p className="text-xs text-red-500 mt-1">{errors.adres}</p>}
        </div>

        {/* Sipariş Notu */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-coffee-medium mb-1.5">Sipariş Notu</label>
          <textarea
            value={data.notlar}
            onChange={e => onChange({ ...data, notlar: e.target.value })}
            placeholder="Sipariş Notu (isteğe bağlı)"
            rows={3}
            className="w-full px-4 py-3 text-sm bg-cream rounded-xl border border-transparent focus:border-gold focus:outline-none focus:bg-white transition-all text-coffee resize-none"
          />
        </div>

      </div>

      <button
        onClick={onNext}
        className="mt-6 w-full py-3.5 bg-coffee hover:bg-coffee-medium text-white font-semibold rounded-xl transition-colors whitespace-nowrap cursor-pointer"
      >
        Devam Et →
      </button>
    </div>
  );
}
